/**
 * usePropertySelection - Property Selection Management Hook
 *
 * Purpose:
 * Manages selectedListingId state, localStorage persistence, initialization logic, and selection
 * source tracking. Handles migration from legacy localStorage keys and validation of selections.
 * Extracted from SessionContext.jsx to reduce responsibility overload.
 *
 * High-level Workflow:
 * 1. On mount: Initialize selectedListingId from localStorage (migrate legacy key if needed)
 * 2. After properties load: Validate existing selection, clear invalid selections, default to first property
 * 3. On selection change: Persist to localStorage (owner_selected_listing_id)
 * 4. Track selection source and timestamp for debugging
 *
 * Assumptions & Invariants:
 * - selectedListingId is the canonical identifier (numeric, persisted as string in localStorage)
 * - Legacy localStorage key (selectedPropertyId) is migrated to owner_selected_listing_id
 * - Selection is validated against available properties before use
 * - Invalid selections are cleared from localStorage
 * - Selection source tracking is for debugging only
 *
 * Dependencies:
 * Internal:
 *   - utils/sessionContextUtils.js: migrateSelectedListingId()
 * External:
 *   - react: useState, useEffect, useCallback, useMemo
 *
 * Version: 1.0.0
 *
 * Changelog:
 * - 2026-01-26: Phase 3 - Extracted from SessionContext.jsx (verbatim, no logic changes)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { migrateSelectedListingId } from '../utils/sessionContextUtils.js';

/**
 * usePropertySelection - Property selection management
 * 
 * @param {Array} properties - All properties from API
 * @param {Array} availableProperties - Access-scoped properties user can access
 * @param {boolean} propertiesLoading - Loading state for properties
 * @param {boolean} debug - Debug mode flag
 * @returns {Object} Selection state and setters
 */
export function usePropertySelection(properties, availableProperties, propertiesLoading, debug) {
  // State: selectedListingId (number) is the canonical identifier
  const [selectedListingIdState, setSelectedListingIdStateInternal] = useState(() => migrateSelectedListingId());
  
  // PHASE 1: Selection tracking for debugging
  const [lastSelectionSource, setLastSelectionSource] = useState(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('owner_selected_listing_id') : null;
    return stored ? 'localstorage_restore' : 'init_default';
  });
  const [lastSelectionAt, setLastSelectionAt] = useState(() => new Date().toISOString());
  const [providerInstanceId] = useState(() => {
    // Generate a unique ID for this provider instance (for debugging duplicate providers)
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID().substring(0, 8);
    }
    // Fallback for SSR or older browsers
    return `p${Math.random().toString(36).substring(2, 10)}`;
  });
  
  // Wrapper around setSelectedListingIdStateInternal to track source
  const setSelectedListingId = useCallback((newId, source = 'unknown') => {
    const oldId = selectedListingIdState;
    setSelectedListingIdStateInternal(newId);
    setLastSelectionSource(source);
    setLastSelectionAt(new Date().toISOString());
    
    if (debug) {
      const stack = new Error().stack;
      const stackLine = stack?.split('\n')[2]?.trim() || 'unknown';
      console.log('[OWNER_SELECT] setSelectedListingId:', {
        oldId,
        newId,
        source,
        stackHint: stackLine,
        timestamp: new Date().toISOString(),
      });
    }
  }, [selectedListingIdState, debug]);

  /**
   * Property Selection Initialization Effect - Initialize and validate selectedListingId
   * 
   * Intent: Initializes selectedListingId after properties load, validates existing selection,
   * and defaults to first available property if no valid selection exists. Handles localStorage
   * migration and cleanup of invalid selections.
   * 
   * Logic:
   * 1. If no properties available: Clear invalid localStorage selection if properties loaded but empty
   * 2. If selection exists and is valid: Keep it, just ensure persistence (don't reset user selection)
   * 3. If selection exists but is invalid: Clear it from localStorage, fall through to default
   * 4. If no selection: Default to first accessible property's listing_id
   * 
   * Risk: Complex dependency array [availableProperties.length, selectedListingIdState, availableProperties,
   * properties.length, propertiesLoading, debug, setSelectedListingId] may cause unnecessary re-runs.
   * availableProperties is memoized but setSelectedListingId depends on selectedListingIdState which may
   * change frequently. Monitor for infinite loops.
   * 
   * Dependencies: [availableProperties.length, selectedListingIdState, availableProperties, properties.length, propertiesLoading, debug, setSelectedListingId]
   */
  // Initialize selectedListingId after properties load - ONLY when no valid selection exists
  useEffect(() => {
    if (availableProperties.length === 0) {
      // CRITICAL FIX: If properties loaded but are empty, clear invalid localStorage selection
      if (properties.length === 0 && !propertiesLoading && selectedListingIdState !== null) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('owner_selected_listing_id');
        }
        if (debug) {
          console.log('[SessionContext] Cleared invalid selectedListingId (no properties available)');
        }
      }
      return;
    }
    
    // If selectedListingId is already set and exists in accessible properties, keep it (don't reset!)
    if (selectedListingIdState !== null && selectedListingIdState !== undefined) {
      const exists = availableProperties.some(p => Number(p.listing_id) === Number(selectedListingIdState));
      if (exists) {
        // Valid selection exists - just ensure it's persisted (but don't change state)
        if (typeof window !== 'undefined') {
          localStorage.setItem('owner_selected_listing_id', String(selectedListingIdState));
        }
        if (debug) {
          console.log('[OWNER_SELECT] [SessionContext] Valid selectedListingId exists, not resetting:', selectedListingIdState);
        }
        return; // CRITICAL: Don't reset user selection!
      } else {
        // CRITICAL FIX: Invalid selection (doesn't exist in available properties) - clear it
        if (typeof window !== 'undefined') {
          localStorage.removeItem('owner_selected_listing_id');
        }
        if (debug) {
          console.log('[SessionContext] Cleared invalid selectedListingId (not in available properties):', selectedListingIdState);
        }
        // Fall through to set default selection
      }
    }
    
    // No valid selection => default to first accessible property's listing_id (ONLY once)
    if (availableProperties.length > 0 && availableProperties[0].listing_id) {
      const defaultListingId = Number(availableProperties[0].listing_id);
      if (Number.isFinite(defaultListingId) && defaultListingId > 0) {
        setSelectedListingId(defaultListingId);
        if (typeof window !== 'undefined') {
          localStorage.setItem('owner_selected_listing_id', String(defaultListingId));
        }
        if (debug) {
          console.log('[SessionContext] Initialized selectedListingId to first property:', defaultListingId);
        }
      }
    }
  }, [availableProperties.length, selectedListingIdState, availableProperties, properties.length, propertiesLoading, debug, setSelectedListingId]);

  // Get selected property object by listing_id (canonical identifier)
  const selectedProperty = useMemo(() => {
    const found = properties.find(p => Number(p.listing_id) === Number(selectedListingIdState)) || 
                  availableProperties[0] || 
                  null;
    if (debug) {
      console.log('[OWNER_SELECT][CTX] selectedProperty computed:', {
        selectedListingId: selectedListingIdState,
        propertiesCount: properties.length,
        availablePropertiesCount: availableProperties.length,
        found: found ? { listing_id: found.listing_id, name: found.name || found.property_name } : null,
        timestamp: new Date().toISOString(),
      });
    }
    return found;
  }, [properties, availableProperties, selectedListingIdState, debug]);

  /**
   * Property Selection Persistence Effect - Persist selectedListingId to localStorage
   * 
   * Intent: Persists selectedListingId to localStorage whenever it changes. This is a pure
   * persistence effect - it does not update state, only writes to localStorage. Ensures
   * user selection survives page refreshes.
   * 
   * Storage Key: 'owner_selected_listing_id' (numeric ID stored as string)
   * Legacy Key: 'selectedPropertyId' (migrated in migrateSelectedListingId, cleaned up here)
   * 
   * Dependencies: [selectedListingIdState, debug, lastSelectionSource, lastSelectionAt]
   */
  // Persist selectedListingId to localStorage when it changes (ONLY persistence, no state updates)
  useEffect(() => {
    if (selectedListingIdState !== null && selectedListingIdState !== undefined && typeof window !== 'undefined') {
      localStorage.setItem('owner_selected_listing_id', String(selectedListingIdState));
      if (debug) {
        console.log('[OWNER_SELECT] [SessionContext] Persisted selectedListingId to localStorage:', {
          selectedListingId: selectedListingIdState,
          source: lastSelectionSource,
          timestamp: lastSelectionAt,
        });
      }
    }
  }, [selectedListingIdState, debug, lastSelectionSource, lastSelectionAt]);

  return {
    selectedListingId: selectedListingIdState,
    setSelectedListingId,
    selectedProperty,
    lastSelectionSource,
    lastSelectionAt,
    providerInstanceId,
  };
}
